import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from './store/unistore/combined/react';
import * as Sentry from '@sentry/browser';
import { I18nextProvider } from 'react-i18next';

import App from './App';
import store from './store/store';
import i18n from './i18n/config';

import OptimizelyWrapper from './OptimizelyWrapper';
import watchCrossTabLogout from './utils/watchCrossTabLogout';

import './styles/variables.scss';
import './styles/layout.scss';
import './styles/header.scss';
import './styles/menus.scss';
import './styles/loading.scss';

import packageJson from '../package.json';

const version = packageJson.version;
const SENTRY_ENABLED_ROUTES = [
    /^\/c\/[^/]+\/?$/, // homepage: /c/:id
];

const isSentryEnabledForPath = pathname =>
    SENTRY_ENABLED_ROUTES.some(pattern => pattern.test(pathname));

if (window.Cypress) {
    window.__store__ = store;
}

if (!__DEV__) {
    Sentry.init({
        dsn: 'https://f774a9857a1d442f8c8ad604eaacc4da@sentry.io/1372224',
        release: __PROD_API__ ? version : __HASHPATH__,
        environment: __PROD_API__ ? 'production' : 'staging',
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false,
                beforeErrorSampling: event => {
                    try {
                        const exception =
                            event.error || event.originalException || event;

                        const isFailedToFetch =
                            event.message?.includes('Failed to fetch') ||
                            event.exception?.values?.[0]?.value?.includes(
                                'Failed to fetch',
                            ) ||
                            exception?.message?.includes('Failed to fetch');

                        return !isFailedToFetch;
                    } catch {
                        // Safe default: skip replay sampling when callback fails.
                        return false;
                    }
                },
            }),
        ],
        beforeSend(event) {
            if (isSentryEnabledForPath(window.location.pathname)) {
                event.tags = {
                    ...event.tags,
                    tracked_homepage: 'true',
                };
            }

            return event;
        },
    });

    window.__triggerSentryTestEvent__ = () => {
        Sentry.withScope(scope => {
            scope.setTag('manual_sentry_test', 'true');
            scope.setTag('current_path', window.location.pathname);
            Sentry.captureMessage('Manual Sentry homepage-tag test event');
        });
    };
}

watchCrossTabLogout(() => Boolean(store.getState().user?.isLoggedIn));

const root = createRoot(document.getElementById('app'));
root.render(
    <Provider store={store}>
        <I18nextProvider i18n={i18n}>
            <OptimizelyWrapper>
                <App />
            </OptimizelyWrapper>
        </I18nextProvider>
    </Provider>,
);
